import React, { Fragment } from 'react';
import { HomeIcon, DatabaseIcon, BellIcon, FileTextIcon, TrendingUpIcon, BarChartIcon, UsersIcon, ServerIcon, ShieldIcon, AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon, WrenchIcon, ClipboardListIcon, LayoutGridIcon } from 'lucide-react';
import { UserRole, ROLE_ACCESS } from '../../types/auth';
interface SidebarProps {
  activePage: string;
  setActivePage: (page: string) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  userRole: UserRole;
}
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  group?: string;
}
export const Sidebar: React.FC<SidebarProps> = ({
  activePage,
  setActivePage,
  collapsed,
  setCollapsed,
  userRole
}) => {
  const allNavItems: NavItem[] = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: <HomeIcon size={20} data-id="element-675" />,
    group: 'main'
  }, {
    id: 'assets',
    label: 'Manajemen Aset',
    icon: <DatabaseIcon size={20} data-id="element-676" />,
    group: 'main'
  }, {
    id: 'reminders',
    label: 'Pengingat & RKA',
    icon: <BellIcon size={20} data-id="element-677" />,
    group: 'ops'
  }, {
    id: 'maintenance',
    label: 'Pemeliharaan',
    icon: <WrenchIcon size={20} data-id="element-678" />,
    group: 'ops'
  }, {
    id: 'contracts',
    label: 'Kontrak & Pengadaan',
    icon: <FileTextIcon size={20} data-id="element-679" />,
    group: 'ops'
  }, {
    id: 'recommendations',
    label: 'Rekomendasi AI',
    icon: <TrendingUpIcon size={20} data-id="element-680" />,
    group: 'analytics'
  }, {
    id: 'analytics',
    label: 'Summary & Analitik',
    icon: <BarChartIcon size={20} data-id="element-681" />,
    group: 'analytics'
  }, {
    id: 'users',
    label: 'Manajemen Pengguna',
    icon: <UsersIcon size={20} data-id="element-682" />,
    group: 'admin'
  }, {
    id: 'server',
    label: 'Status Server',
    icon: <ServerIcon size={20} data-id="element-683" />,
    group: 'admin'
  }, {
    id: 'audit',
    label: 'Audit & Kepatuhan',
    icon: <ShieldIcon size={20} data-id="element-684" />,
    group: 'admin'
  }, {
    id: 'notifications',
    label: 'Notifikasi',
    icon: <AlertCircleIcon size={20} data-id="element-685" />,
    group: 'support'
  }, {
    id: 'reporting',
    label: 'Pelaporan',
    icon: <ClipboardListIcon size={20} data-id="element-686" />,
    group: 'support'
  }, {
    id: 'taskboard',
    label: 'Task & Ticketing',
    icon: <LayoutGridIcon size={20} data-id="element-687" />,
    group: 'support'
  }];
  // Filter nav items based on user role
  const allowedPages = ROLE_ACCESS[userRole] || [];
  const navItems = allNavItems.filter(item => allowedPages.includes(item.id));
  // Group items logic
  const renderNavItems = () => {
    let lastGroup = '';
    return navItems.map((item, index) => {
      const showSeparator = lastGroup && item.group !== lastGroup;
      lastGroup = item.group || '';
      return <Fragment key={item.id} data-id="element-688">
          {showSeparator && !collapsed && <div className="my-2 mx-4 border-t border-dark-700/50" data-id="element-689"></div>}
          <li className="mb-1" data-id="element-690">
            <button onClick={() => setActivePage(item.id)} className={`flex items-center w-full p-2.5 transition-all duration-200 group relative ${activePage === item.id ? 'bg-dark-700 text-white' : 'text-gray-400 hover:bg-dark-700/50 hover:text-gray-200'} ${collapsed ? 'justify-center rounded-lg mx-auto w-10 h-10 p-0' : 'justify-start rounded-r-lg mr-2'}`} aria-label={item.label} title={collapsed ? item.label : undefined} data-id="element-691">
              {/* Active Indicator Border (Left) */}
              {!collapsed && activePage === item.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full shadow-[0_0_8px_rgba(242,195,0,0.5)]" data-id="element-692"></div>}

              <span className={`flex-shrink-0 transition-colors ${activePage === item.id ? 'text-primary' : 'group-hover:text-white'}`} data-id="element-693">
                {item.icon}
              </span>

              {!collapsed && <span className={`ml-3 truncate text-sm font-medium ${activePage === item.id ? 'text-white' : ''}`} data-id="element-694">
                  {item.label}
                </span>}

              {/* Tooltip for collapsed state */}
              {collapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-dark-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 border border-dark-700 shadow-xl" data-id="element-695">
                  {item.label}
                </div>}
            </button>
          </li>
        </Fragment>;
    });
  };
  return <aside className={`bg-dark-800 border-r border-dark-700 text-gray-300 transition-all duration-300 ease-in-out flex-shrink-0 flex flex-col ${collapsed ? 'w-16' : 'w-64'} ${collapsed ? 'absolute lg:relative' : 'relative'} h-full z-30 shadow-xl`} data-id="element-696">
      <div className="flex justify-between items-center h-16 px-4 border-b border-dark-700 bg-dark-900/50 relative overflow-hidden" data-id="element-697">
        {/* Header Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" data-id="element-698"></div>

        {!collapsed && <div className="flex flex-col min-w-0 animate-fadeIn" data-id="element-699">
            <h2 className="text-white font-bold text-lg tracking-tight flex items-center" data-id="element-700">
              <span className="text-primary mr-1" data-id="element-701">ARSENA</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30 ml-2" data-id="element-702">
                v2.0
              </span>
            </h2>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider truncate mt-0.5" data-id="element-703">
              Strategic Enterprise Network
            </p>
          </div>}
        <button onClick={() => setCollapsed(!collapsed)} className={`p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-dark-700 transition-colors flex-shrink-0 ${collapsed ? 'mx-auto' : ''}`} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-704">
          {collapsed ? <ChevronRightIcon size={18} data-id="element-705" /> : <ChevronLeftIcon size={18} data-id="element-706" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 scrollbar-dark" data-id="element-707">
        <ul className="px-2" data-id="element-708">{renderNavItems()}</ul>
      </nav>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-dark-700 bg-dark-900/30" data-id="element-709">
        {!collapsed ? <div className="text-[10px] text-gray-600 text-center font-mono" data-id="element-710">
            SECURE CONNECTION
            <br data-id="element-711" />
            <span className="text-green-500" data-id="element-712">● ONLINE</span>
          </div> : <div className="flex justify-center" data-id="element-713">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-714"></div>
          </div>}
      </div>
    </aside>;
};